import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * 🤖 Behavioral Analysis Hook
 *
 * Monitora o comportamento do usuário para detectar padrões suspeitos de bots
 *
 * Métricas monitoradas:
 * - Tempo na página antes de submeter formulário
 * - Movimentação do mouse (trajetória, velocidade)
 * - Velocidade de digitação (muito rápida = bot)
 * - Sequência de interações com campos
 * - Padrões de preenchimento (linear vs. humano)
 * - Copy/paste detectado
 * - Tempo entre cliques
 *
 * Score de confiança: 0-100 (0 = bot, 100 = humano)
 */

export interface BehavioralMetrics {
  // Tempo
  timeOnPage: number              // Milissegundos na página
  timeSinceFirstInteraction: number

  // Mouse
  mouseMovements: number          // Quantidade de movimentos
  mouseClicks: number
  averageMouseSpeed: number       // px/ms
  mouseIdleTime: number          // Tempo sem mover mouse

  // Teclado
  keystrokes: number
  averageTypingSpeed: number     // ms entre teclas
  copyPasteDetected: boolean

  // Interações
  fieldInteractions: number      // Quantos campos foram focados
  formCompletionTime: number     // Tempo para preencher formulário
  fieldSequence: string[]        // Ordem de preenchimento dos campos

  // Padrões suspeitos
  suspiciousPatterns: string[]   // Lista de padrões detectados
  confidenceScore: number        // 0-100 (humano = 80-100, bot = 0-50)
}

export interface BehavioralAnalysisResult {
  metrics: BehavioralMetrics
  isLikelyBot: boolean
  confidenceScore: number
  suspiciousPatterns: string[]
  recommendation: 'allow' | 'challenge' | 'block'
}

const THRESHOLDS = {
  MIN_TIME_ON_PAGE: 2000,           // 2 segundos (bots preenchem instantaneamente)
  MIN_MOUSE_MOVEMENTS: 10,          // Mínimo de movimentos de mouse
  MAX_TYPING_SPEED: 50,             // 50ms entre teclas = muito rápido
  MIN_TYPING_SPEED: 2000,           // 2s entre teclas = copiar/colar
  MIN_FORM_COMPLETION_TIME: 3000,   // 3 segundos mínimo para preencher
  MIN_FIELD_INTERACTIONS: 2,        // Pelo menos 2 campos interagidos
  CONFIDENCE_THRESHOLD_ALLOW: 70,   // Score >= 70 = allow
  CONFIDENCE_THRESHOLD_BLOCK: 40,   // Score < 40 = block
}

export function useBehavioralAnalysis() {
  const [metrics, setMetrics] = useState<BehavioralMetrics>({
    timeOnPage: 0,
    timeSinceFirstInteraction: 0,
    mouseMovements: 0,
    mouseClicks: 0,
    averageMouseSpeed: 0,
    mouseIdleTime: 0,
    keystrokes: 0,
    averageTypingSpeed: 0,
    copyPasteDetected: false,
    fieldInteractions: 0,
    formCompletionTime: 0,
    fieldSequence: [],
    suspiciousPatterns: [],
    confidenceScore: 50, // Neutro inicialmente
  })

  const pageLoadTime = useRef<number>(Date.now())
  const firstInteractionTime = useRef<number | null>(null)
  const lastMousePosition = useRef<{ x: number; y: number; time: number } | null>(null)
  const lastKeyTime = useRef<number | null>(null)
  const keyTimings = useRef<number[]>([])
  const interactedFields = useRef<Set<string>>(new Set())
  const fieldSequence = useRef<string[]>([])

  // 🖱️ Monitorar movimentos do mouse
  useEffect(() => {
    let mouseMoveCount = 0
    let totalSpeed = 0
    let speedSamples = 0
    let lastMoveTime = Date.now()

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now()

      if (!firstInteractionTime.current) {
        firstInteractionTime.current = now
      }

      // Calcular velocidade do mouse
      if (lastMousePosition.current) {
        const dx = e.clientX - lastMousePosition.current.x
        const dy = e.clientY - lastMousePosition.current.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        const timeDelta = now - lastMousePosition.current.time

        if (timeDelta > 0) {
          const speed = distance / timeDelta
          totalSpeed += speed
          speedSamples++
        }
      }

      lastMousePosition.current = { x: e.clientX, y: e.clientY, time: now }
      lastMoveTime = now
      mouseMoveCount++

      setMetrics(prev => ({
        ...prev,
        mouseMovements: mouseMoveCount,
        averageMouseSpeed: speedSamples > 0 ? totalSpeed / speedSamples : 0,
        mouseIdleTime: 0,
      }))
    }

    const handleMouseClick = () => {
      if (!firstInteractionTime.current) {
        firstInteractionTime.current = Date.now()
      }

      setMetrics(prev => ({
        ...prev,
        mouseClicks: prev.mouseClicks + 1,
      }))
    }

    // Detectar mouse idle (bot não move mouse)
    const idleInterval = setInterval(() => {
      const idleTime = Date.now() - lastMoveTime
      setMetrics(prev => ({
        ...prev,
        mouseIdleTime: idleTime,
      }))
    }, 1000)

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('click', handleMouseClick)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('click', handleMouseClick)
      clearInterval(idleInterval)
    }
  }, [])

  // ⌨️ Monitorar digitação
  useEffect(() => {
    const handleKeyDown = () => {
      const now = Date.now()

      if (!firstInteractionTime.current) {
        firstInteractionTime.current = now
      }

      if (lastKeyTime.current) {
        const timeDelta = now - lastKeyTime.current
        keyTimings.current.push(timeDelta)
      }

      lastKeyTime.current = now

      const avgTypingSpeed = keyTimings.current.length > 0
        ? keyTimings.current.reduce((a, b) => a + b, 0) / keyTimings.current.length
        : 0

      setMetrics(prev => ({
        ...prev,
        keystrokes: prev.keystrokes + 1,
        averageTypingSpeed: avgTypingSpeed,
      }))
    }

    // Detectar copy/paste
    const handlePaste = () => {
      setMetrics(prev => ({
        ...prev,
        copyPasteDetected: true,
      }))
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('paste', handlePaste)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('paste', handlePaste)
    }
  }, [])

  // 📝 Atualizar tempo na página
  useEffect(() => {
    const interval = setInterval(() => {
      const timeOnPage = Date.now() - pageLoadTime.current
      const timeSinceFirst = firstInteractionTime.current
        ? Date.now() - firstInteractionTime.current
        : 0

      setMetrics(prev => ({
        ...prev,
        timeOnPage,
        timeSinceFirstInteraction: timeSinceFirst,
      }))
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // 📋 Rastrear interações com campos do formulário
  const trackFieldInteraction = useCallback((fieldName: string) => {
    if (!interactedFields.current.has(fieldName)) {
      interactedFields.current.add(fieldName)
      fieldSequence.current.push(fieldName)

      setMetrics(prev => ({
        ...prev,
        fieldInteractions: interactedFields.current.size,
        fieldSequence: [...fieldSequence.current],
      }))
    }
  }, [])

  // 🎯 Calcular score de confiança e detectar padrões suspeitos
  const analyze = useCallback((): BehavioralAnalysisResult => {
    const suspiciousPatterns: string[] = []
    let score = 100 // Começa com 100 (humano), subtrai pontos por comportamentos suspeitos

    // 🚨 PADRÃO 1: Tempo na página muito curto
    if (metrics.timeOnPage < THRESHOLDS.MIN_TIME_ON_PAGE) {
      suspiciousPatterns.push('very_fast_submission')
      score -= 30
    }

    // 🚨 PADRÃO 2: Sem movimentos de mouse
    if (metrics.mouseMovements < THRESHOLDS.MIN_MOUSE_MOVEMENTS) {
      suspiciousPatterns.push('no_mouse_movement')
      score -= 25
    }

    // 🚨 PADRÃO 3: Velocidade de digitação inumana
    if (metrics.keystrokes > 5 && metrics.averageTypingSpeed < THRESHOLDS.MAX_TYPING_SPEED) {
      suspiciousPatterns.push('typing_too_fast')
      score -= 20
    }

    // 🚨 PADRÃO 4: Copiar/colar tudo (possível autofill de bot)
    if (metrics.copyPasteDetected && metrics.keystrokes < 10) {
      suspiciousPatterns.push('excessive_copy_paste')
      score -= 15
    }

    // 🚨 PADRÃO 5: Formulário preenchido muito rápido
    if (metrics.timeSinceFirstInteraction < THRESHOLDS.MIN_FORM_COMPLETION_TIME && metrics.fieldInteractions >= 2) {
      suspiciousPatterns.push('form_completed_too_fast')
      score -= 20
    }

    // 🚨 PADRÃO 6: Poucos campos interagidos (bot pode pular validação)
    if (metrics.fieldInteractions < THRESHOLDS.MIN_FIELD_INTERACTIONS) {
      suspiciousPatterns.push('insufficient_field_interactions')
      score -= 15
    }

    // 🚨 PADRÃO 7: Mouse idle por muito tempo (bot não move)
    if (metrics.mouseIdleTime > 5000 && metrics.timeOnPage > 5000) {
      suspiciousPatterns.push('mouse_idle_too_long')
      score -= 10
    }

    // 🚨 PADRÃO 8: Preenchimento linear perfeito (humano pula campos)
    const isLinearFill = fieldSequence.current.length > 2 &&
      fieldSequence.current.every((field, i) =>
        i === 0 || field > fieldSequence.current[i - 1]
      )
    if (isLinearFill) {
      suspiciousPatterns.push('perfect_linear_fill')
      score -= 10
    }

    // ✅ BÔNUS: Comportamento humano natural
    if (metrics.mouseMovements > 50) score += 5
    if (metrics.keystrokes > 20 && metrics.averageTypingSpeed > 100) score += 5

    // Garantir score entre 0-100
    score = Math.max(0, Math.min(100, score))

    // 📊 Determinar recomendação
    let recommendation: 'allow' | 'challenge' | 'block'
    if (score >= THRESHOLDS.CONFIDENCE_THRESHOLD_ALLOW) {
      recommendation = 'allow'
    } else if (score >= THRESHOLDS.CONFIDENCE_THRESHOLD_BLOCK) {
      recommendation = 'challenge' // Requer CAPTCHA adicional
    } else {
      recommendation = 'block'
    }

    const isLikelyBot = score < THRESHOLDS.CONFIDENCE_THRESHOLD_BLOCK

    return {
      metrics: {
        ...metrics,
        suspiciousPatterns,
        confidenceScore: score,
      },
      isLikelyBot,
      confidenceScore: score,
      suspiciousPatterns,
      recommendation,
    }
  }, [metrics])

  return {
    metrics,
    analyze,
    trackFieldInteraction,
  }
}
